import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    startUrl: String
  }

  async start(event) {
    event.preventDefault()

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content

    this._showGeneratingToast("Preparing the combined bid export. This can take a moment for large projects.")

    try {
      const resp = await fetch(this.startUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrf
        },
        credentials: "same-origin"
      })

      if (!resp.ok) {
        this._hideGeneratingToast()
        this._showToast("Couldn't start the export. Please try again.", "alert")
        return
      }

      const data = await resp.json()
      if (!data.status_url) {
        this._hideGeneratingToast()
        this._showToast("Couldn't start the export. Please try again.", "alert")
        return
      }

      this._poll(data.status_url)
    } catch (e) {
      console.error("All bids export failed to start:", e)
      this._hideGeneratingToast()
      this._showToast("Couldn't start the export. Please try again.", "alert")
    }
  }

  async _poll(statusUrl, attempts = 0) {
    // Up to ~10 minutes at 3s cadence. Large bid sets can take a while.
    if (attempts > 200) {
      this._hideGeneratingToast()
      this._showToast("The export is taking longer than expected. Please try again.", "alert")
      return
    }

    await new Promise(r => setTimeout(r, 3000))

    try {
      const resp = await fetch(statusUrl, { credentials: "same-origin", headers: { "Accept": "application/json" } })
      const data = await resp.json()

      if (data.status === "ready" && data.url) {
        this._hideGeneratingToast()
        this._showToast("Bid export ready — downloading now.", "notice")
        await this._downloadBlob(data.url, data.filename || "all-bids.pdf")
        return
      }

      if (data.status === "error") {
        this._hideGeneratingToast()
        this._showToast(data.message || "Export failed. Please try again.", "alert")
        return
      }

      this._poll(statusUrl, attempts + 1)
    } catch (e) {
      console.error("All bids export poll failed:", e)
      this._poll(statusUrl, attempts + 1)
    }
  }

  async _downloadBlob(url, filename) {
    try {
      const blob = await fetch(url).then(r => r.blob())
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = objectUrl
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(objectUrl)
    } catch (e) {
      console.error("All bids export download failed:", e)
      this._showToast("Export ready but the download failed. Please try again.", "alert")
    }
  }

  _showGeneratingToast(message) {
    this._hideGeneratingToast()
    const el = document.createElement("div")
    el.id = "all-bids-export-generating-toast"
    el.className = "flash-toast-info"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "0")
    el.setAttribute("role", "status")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = `<svg class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>`
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _hideGeneratingToast() {
    document.getElementById("all-bids-export-generating-toast")?.remove()
  }

  _showToast(message, type) {
    const cssType = (type === "alert" || type === "error") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "status")
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
